import { NetworkStatus } from '@apollo/client';
import type React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { Maybe } from '@xing-com/crate-common-graphql-types';
import { useXingUserFeatures } from '@xing-com/crate-hooks-use-xing-user-features';

import { CompanyCardContainer } from '../../components/company-card-container';
import { MetaData } from '../../components/meta-data';
import { PageError } from '../../components/page-error';
import { ShowMoreButton } from '../../components/show-more-button';
import { CompanyCardsSkeletonLoggedin } from '../../components/skeleton';
import { useCompanyData } from '../../hooks/use-company-data/use-company-data';
import { useDeleteCompanyRecommendation } from '../../hooks/use-delete-company-recommendation';
import { useRecommendedCompanies } from '../../hooks/use-recommended-companies';
import { useTrackPage } from '../../hooks/use-track-page';
import {
  pagesTrackingName,
  trackViewMoreRecommendedCompanies,
  useNwtTrackingCompanies,
} from '../../utils/tracking';
import * as Styled from './recommended-companies.styles';
import RecommendedCompanyCard from './recommended-company-card';

export const RecommendedCompanies: React.FC = () => {
  const intl = useIntl();
  const { isBrandManagerEditor } = useXingUserFeatures();
  const { addCompanies } = useCompanyData();

  const { data, networkStatus, loading, error, pagination } =
    useRecommendedCompanies();

  const [deletedCompanies, setDeletedCompanies] = useState<number>(0);

  const { handleDeleteRecommendation } = useDeleteCompanyRecommendation({
    setDeletedCompanies,
  });

  const trackNwtEvent = useNwtTrackingCompanies();

  useTrackPage({ pageName: pagesTrackingName.recommended });
  useEffect(() => {
    trackNwtEvent({
      eventSchema: 'basic',
      page: 'companies/overview',
      event: 'viewed_screen',
    });
  }, []);

  const companiesRecommendations = useMemo(() => {
    if (!data?.viewer?.companiesRecommendations?.edges) return undefined;

    return data?.viewer?.companiesRecommendations?.edges
      ?.map((item) => item?.node)
      .filter((item) => item);
  }, [data?.viewer?.companiesRecommendations?.edges]);

  useEffect(() => {
    if (companiesRecommendations) {
      addCompanies({
        companies: companiesRecommendations.map(
          (item) => item?.company
        ) as $TSFixMe,
      });
    }
  }, [companiesRecommendations]);

  const loadingFirstRender =
    loading && networkStatus !== NetworkStatus.fetchMore;
  if (loadingFirstRender) return <CompanyCardsSkeletonLoggedin />;
  if (error) return <PageError />;

  const { hasNextPage, endCursor } =
    data?.viewer?.companiesRecommendations?.pageInfo ?? {};

  const isPaginationDisabled = () => loadingFirstRender;

  return (
    <>
      <MetaData
        index={false}
        title={intl.formatMessage({ id: 'CP_RECOMMENDED_COMPANIES_HEADER' })}
        description={intl.formatMessage({
          id: 'CP_RECOMMENDED_COMPANIES_HEADER',
        })}
      />
      <Styled.Headline size="medium">
        <FormattedMessage id="CP_RECOMMENDED_COMPANIES_HEADER" />
      </Styled.Headline>
      <CompanyCardContainer>
        {companiesRecommendations?.map((node, index) => (
          <RecommendedCompanyCard
            key={node!.company?.id}
            recommendedCompany={node as $TSFixMe}
            position={index}
            onRemoveButtonClick={(company) => {
              handleDeleteRecommendation(company?.id);
              trackNwtEvent({
                eventSchema: 'basic',
                page: 'companies/overview',
                event: 'discarded',
                itemUrn: `surn:x-xing:companies:company:${node!.company?.id}`,
              });
            }}
            onClick={() =>
              trackNwtEvent({
                eventSchema: 'basic',
                event: 'opened',
                element: 'company_card',
                page: 'companies/overview',
                position: index,
                itemUrn: `surn:x-xing:companies:company:${node!.company?.id}`,
              })
            }
          />
        ))}
      </CompanyCardContainer>
      {hasNextPage && (
        <ShowMoreButton
          disabled={isPaginationDisabled()}
          loading={loading}
          onClick={() => {
            pagination(
              endCursor as Maybe<string>,
              deletedCompanies,
              setDeletedCompanies
            );
            trackViewMoreRecommendedCompanies({
              userIsEditor: isBrandManagerEditor,
            });
            trackNwtEvent({
              eventSchema: 'basic',
              page: 'companies/overview',
              event: 'clicked',
              element: 'show_more',
            });
          }}
          intlKey="CP_OTHER_INTERESTING_COMPANIES"
        />
      )}
    </>
  );
};
